import React, { createContext, useContext, useState } from 'react';
import globalWindow from '@shared/core/globals';

/**
 * How long one gallery placement lasts. The public gallery's randomized placement (promo columns,
 * concierge tile order) is derived from the current period, so it is the same for every visitor
 * within a period and changes when the next one starts (PRNT-5494). Change this to make placement
 * rotate faster or slower; nothing else depends on the length.
 */
export const PLACEMENT_PERIOD_MS = 60 * 60 * 1000;

/**
 * Which period `now` falls in: the number of whole periods since the Unix epoch. Epoch-based, so
 * every server agrees regardless of its time zone, and every moment inside a period maps to the
 * same number.
 */
export const getPlacementPeriod = (now: number = Date.now()): number => Math.floor(now / PLACEMENT_PERIOD_MS);

/**
 * The period the server inlined (Html.tsx). Typed here rather than on the global `Window` in
 * typings/index.d.ts: any edit to that file makes CI's storybook type-check recheck everything,
 * which now runs out of memory. Move it there once that check has the headroom.
 */
export type WindowWithGalleryPlacementPeriod = { __INITIAL_GALLERY_PLACEMENT_PERIOD__?: number };

const GalleryPlacementPeriodContext = createContext<number | undefined>(undefined);

type GalleryPlacementPeriodProviderProps = Readonly<{
  /** Server only: the period this request computed once, so every render pass agrees. */
  initialGalleryPlacementPeriod?: number;
  children: React.ReactNode;
}>;

/**
 * Fixes the placement period for the life of the page.
 *
 * The client never reads its own clock for a server-rendered page: by the time it hydrates it may
 * be in the next period, and a page served from a cache may be hours old. Either way its placement
 * would differ from the server HTML. So the server inlines the period it rendered with
 * (`window.__INITIAL_GALLERY_PLACEMENT_PERIOD__`) and the client adopts it. It is also kept for
 * later in-app navigation, so the gallery doesn't reshuffle mid-visit at a period boundary.
 * Only a page with no server render computes the period itself.
 */
export const GalleryPlacementPeriodProvider = ({ initialGalleryPlacementPeriod, children }: GalleryPlacementPeriodProviderProps) => {
  const [placementPeriod] = useState(
    () => initialGalleryPlacementPeriod ?? (globalWindow as typeof globalWindow & WindowWithGalleryPlacementPeriod).__INITIAL_GALLERY_PLACEMENT_PERIOD__ ?? getPlacementPeriod(),
  );
  return <GalleryPlacementPeriodContext.Provider value={placementPeriod}>{children}</GalleryPlacementPeriodContext.Provider>;
};

/** The page's placement period. Outside the provider (tests, Storybook) it falls back to the current one. */
export const useGalleryPlacementPeriod = (): number => useContext(GalleryPlacementPeriodContext) ?? getPlacementPeriod();
